import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  AutoComplete,
  message,
  Popconfirm,
  Space,
  Card,
  Tag,
  Row,
  Col,
  Radio,
  Divider,
  Upload,
  Image,
  Checkbox,
  DatePicker,
  Tabs,
  Avatar,
  List,
  Spin,
  Empty,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, InboxOutlined, DeleteOutlined as DeleteIcon } from '@ant-design/icons';
import api, { getImageUrl } from '../services/api';
import dayjs from 'dayjs';
import PlanoCarro from '../components/PlanoCarro';
import { useAuth } from '../contexts/AuthContext';
import { departamentos, getProvinciasByDepartamento, getDistritosByProvincia } from '../data/peru';
import { calcularMontosServicio, parseIncluyeIgv } from '../utils/montosServicio';

const Servicios = () => {
  const { user } = useAuth();
  const [servicios, setServicios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [vehiculos, setVehiculos] = useState([]);
  const [tiposServicio, setTiposServicio] = useState([]);
  const [aseguradoras, setAseguradoras] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [personalDisponible, setPersonalDisponible] = useState([]);
  const [asistencias, setAsistencias] = useState([]);
  const [metodosPago, setMetodosPago] = useState([]);
  const [unidades, setUnidades] = useState([]);

  // Filtros
  const [filtroTecnico, setFiltroTecnico] = useState(null);
  const [filtroUnidad, setFiltroUnidad] = useState(null);
  const [filtroFechaInicio, setFiltroFechaInicio] = useState(null);
  const [filtroFechaFin, setFiltroFechaFin] = useState(null);

  // Verificar si es admin principal (sin empresa_id)
  const isMainAdmin = user && user.rol === 'admin' && !user.empresa_id;
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [clienteModalVisible, setClienteModalVisible] = useState(false);
  const [vehiculoModalVisible, setVehiculoModalVisible] = useState(false);
  const [editingServicio, setEditingServicio] = useState(null);
  const [form] = Form.useForm();
  const [clienteForm] = Form.useForm();
  const [vehiculoForm] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [comprobanteAdelanto, setComprobanteAdelanto] = useState([]);
  const [comprobantesPago, setComprobantesPago] = useState([]);
  const [pagosServicio, setPagosServicio] = useState([]);
  const [pagosPendientes, setPagosPendientes] = useState([]); // Pagos agregados antes de guardar el servicio
  const [pagoModalVisible, setPagoModalVisible] = useState(false);
  const [editingPago, setEditingPago] = useState(null);
  const [pagoForm] = Form.useForm();
  const [comprobantesPagoForm, setComprobantesPagoForm] = useState([]);

  // Estados para ubicación (lugar de contacto)
  const [departamentoContacto, setDepartamentoContacto] = useState(null);
  const [provinciaContacto, setProvinciaContacto] = useState(null);
  const [distritoContacto, setDistritoContacto] = useState(null);

  // Estados para ubicación (lugar de destino)
  const [departamentoDestino, setDepartamentoDestino] = useState(null);
  const [provinciaDestino, setProvinciaDestino] = useState(null);
  const [distritoDestino, setDistritoDestino] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [esExtemporaneo, setEsExtemporaneo] = useState(false);
  const [modoSoloLectura, setModoSoloLectura] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [seguimientoMensajes, setSeguimientoMensajes] = useState([]);
  const [cargandoSeguimiento, setCargandoSeguimiento] = useState(false);
  const [nuevoMensaje, setNuevoMensaje] = useState('');
  const [imagenSeguimiento, setImagenSeguimiento] = useState(null);
  const [activeTab, setActiveTab] = useState('1');

  useEffect(() => {
    fetchServicios();
    fetchClientes();
    fetchVehiculos();
    fetchTiposServicio();
    fetchAseguradoras();
    if (isMainAdmin) {
      fetchEmpresas();
    }
    fetchPersonal();
    fetchAsistencias();
    fetchMetodosPago();
    fetchUnidades();
  }, [isMainAdmin]);

  // Filtrar personal disponible (con asistencia activa y sin servicio en curso)
  useEffect(() => {
    const personalConAsistencia = personal.filter(p => {
      // Verificar si tiene asistencia activa (tipo 'trabajo', con hora_ingreso pero sin hora_salida)
      const asistencia = asistencias.find(a =>
        a.personal_id === p.id &&
        a.tipo === 'trabajo' &&
        a.hora_ingreso &&
        !a.hora_salida
      );

      if (!asistencia) {
        return false; // No tiene asistencia activa
      }

      // Verificar que NO esté en un servicio actualmente (estado no es 'completado' ni 'cancelado')
      const servicioActivo = servicios.find(s =>
        s.trabajador_id === p.id &&
        s.estado !== 'completado' &&
        s.estado !== 'cancelado'
      );

      return !servicioActivo; // Solo disponible si no tiene servicio activo
    });
    setPersonalDisponible(personalConAsistencia);
  }, [personal, asistencias, servicios]);

  // Recargar servicios cuando cambien los filtros
  useEffect(() => {
    fetchServicios(searchText);
  }, [filtroTecnico, filtroUnidad, filtroFechaInicio, filtroFechaFin]);

  // Calcular opciones de personal incluyendo el trabajador asignado si está editando
  const opcionesPersonalCompletas = useMemo(() => {
    let opciones = esExtemporaneo ? personal : personalDisponible;

    // Si estamos editando y hay un trabajador_id asignado, asegurarnos de que esté en las opciones
    if (editingServicio?.trabajador_id) {
      const trabajadorIdNum = Number(editingServicio.trabajador_id);
      if (!opciones.find(p => Number(p.id) === trabajadorIdNum)) {
        const trabajadorAsignado = personal.find(p => Number(p.id) === trabajadorIdNum);
        if (trabajadorAsignado) {
          opciones = [...opciones, trabajadorAsignado];
        }
      }
    }

    return opciones;
  }, [esExtemporaneo, personal, personalDisponible, editingServicio]);

  // Calcular opciones de clientes incluyendo el cliente asignado si está editando
  const opcionesClientesCompletas = useMemo(() => {
    let opciones = [...clientes];

    // Si estamos editando y hay un cliente_id asignado, asegurarnos de que esté en las opciones
    if (editingServicio?.cliente_id) {
      const clienteIdNum = Number(editingServicio.cliente_id);
      if (!opciones.find(c => Number(c.id) === clienteIdNum)) {
        // Si el cliente no está en la lista, intentar obtenerlo del servicio actual
        // El backend devuelve cliente_nombre y cliente_apellido, pero no el objeto completo
        // En este caso, creamos un objeto temporal con los datos disponibles
        const servicioActual = servicios.find(s => s.id === editingServicio.id);
        if (servicioActual && servicioActual.cliente_nombre) {
          opciones = [...opciones, {
            id: clienteIdNum,
            nombre: servicioActual.cliente_nombre,
            apellido: servicioActual.cliente_apellido || ''
          }];
        }
      }
    }

    return opciones;
  }, [clientes, editingServicio, servicios]);

  const fetchServicios = async (searchTerm = '') => {
    setLoading(true);
    try {
      const params = {};
      if (searchTerm) {
        params.search = searchTerm;
      }
      if (filtroTecnico) {
        params.trabajador_id = filtroTecnico;
      }
      if (filtroUnidad) {
        params.unidad_id = filtroUnidad;
      }
      if (filtroFechaInicio) {
        params.fecha_inicio = dayjs(filtroFechaInicio).format('YYYY-MM-DD');
      }
      if (filtroFechaFin) {
        params.fecha_fin = dayjs(filtroFechaFin).format('YYYY-MM-DD');
      }
      const response = await api.get('/servicios', { params });
      // Asegurar que siempre sea un array
      setServicios(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar servicios:', error);
      message.error(error.message);
      setServicios([]); // Asegurar que sea un array incluso en caso de error
    } finally {
      setLoading(false);
    }
  };

  const fetchClientes = async () => {
    try {
      const response = await api.get('/clientes');
      setClientes(response.data);
    } catch (error) {
      console.error('Error al cargar clientes');
    }
  };

  const fetchVehiculos = async () => {
    try {
      const response = await api.get('/vehiculos');
      setVehiculos(response.data);
    } catch (error) {
      console.error('Error al cargar vehículos');
    }
  };

  const fetchTiposServicio = async () => {
    try {
      const response = await api.get('/tipos-servicio');
      setTiposServicio(response.data.filter(t => t.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar tipos de servicio');
    }
  };

  const fetchAseguradoras = async () => {
    try {
      const response = await api.get('/aseguradoras');
      setAseguradoras(response.data.filter(a => a.estado === 'activa'));
    } catch (error) {
      console.error('Error al cargar aseguradoras');
    }
  };

  const fetchEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      console.error('Error al cargar empresas');
    }
  };

  const fetchPersonal = async () => {
    try {
      const response = await api.get('/personal');
      setPersonal(response.data.filter(p => p.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar personal');
    }
  };

  const fetchAsistencias = async () => {
    try {
      const response = await api.get('/asistencias');
      setAsistencias(response.data);
    } catch (error) {
      console.error('Error al cargar asistencias');
    }
  };

  const fetchMetodosPago = async () => {
    try {
      const response = await api.get('/metodos-pago');
      setMetodosPago(response.data.filter(m => m.estado === 'activo'));
    } catch (error) {
      console.error('Error al cargar métodos de pago');
    }
  };

  const fetchUnidades = async () => {
    try {
      const response = await api.get('/unidades');
      setUnidades(response.data || []);
    } catch (error) {
      console.error('Error al cargar unidades');
    }
  };


  const fetchPagosServicio = async (servicioId) => {
    try {
      const response = await api.get(`/pagos-servicios?servicio_id=${servicioId}`);
      setPagosServicio(response.data || []);
    } catch (error) {
      console.error('Error al cargar pagos del servicio:', error);
      setPagosServicio([]);
    }
  };

  const handleAgregarPago = () => {
    setEditingPago(null);
    pagoForm.resetFields();
    pagoForm.setFieldsValue({
      fecha_pago: dayjs(),
      estado: 'pendiente'
    });
    setComprobantesPagoForm([]);
    setPagoModalVisible(true);
  };

  const handleEditarPago = (pago) => {
    setEditingPago(pago);
    pagoForm.setFieldsValue({
      monto: pago.monto,
      metodo_pago_id: pago.metodo_pago_id,
      observaciones: pago.observaciones,
      fecha_pago: pago.fecha_pago ? dayjs(pago.fecha_pago) : dayjs(),
      estado: pago.estado
    });

    // Cargar comprobantes existentes
    if (pago.comprobantes && Array.isArray(pago.comprobantes) && pago.comprobantes.length > 0) {
      const comprobantesFiles = pago.comprobantes.map((img, index) => {
        const url = typeof img === 'object' && img.url ? img.url : img;
        return {
          uid: `comprobante-pago-${pago.id}-${index}`,
          name: `comprobante-${index}.jpg`,
          status: 'done',
          url: getImageUrl(url),
          thumbUrl: getImageUrl(url),
          originalUrl: url,
          descripcion: typeof img === 'object' && img.descripcion ? img.descripcion : ''
        };
      });
      setComprobantesPagoForm(comprobantesFiles);
    } else {
      setComprobantesPagoForm([]);
    }

    setPagoModalVisible(true);
  };

  const handleEliminarPago = async (pagoId) => {
    try {
      // Si es un pago pendiente (tiene tempId), eliminarlo del estado local
      if (typeof pagoId === 'string' && pagoId.startsWith('temp-')) {
        setPagosPendientes(pagosPendientes.filter(p => p.tempId !== pagoId));
        message.success('Pago eliminado');
        return;
      }

      // Si es un pago guardado, eliminarlo del backend
      await api.delete(`/pagos-servicios/${pagoId}`);
      message.success('Pago eliminado exitosamente');
      if (editingServicio?.id) {
        fetchPagosServicio(editingServicio.id);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleGuardarPago = async (values) => {
    try {
      // Subir comprobantes si hay archivos nuevos
      let comprobantesUrls = [];
      const archivosNuevos = comprobantesPagoForm.filter(f => f.originFileObj);
      const archivosExistentes = comprobantesPagoForm.filter(f => !f.originFileObj && f.originalUrl);

      if (archivosNuevos.length > 0) {
        const formData = new FormData();
        archivosNuevos.forEach((file) => {
          formData.append('imagenes', file.originFileObj);
        });

        const uploadResponse = await api.post('/servicios/upload-images', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        comprobantesUrls = uploadResponse.data.urls.map((url, index) => {
          const archivo = archivosNuevos[index];
          return {
            url: url,
            descripcion: archivo.descripcion || ''
          };
        });
      }

      // Agregar comprobantes existentes
      archivosExistentes.forEach((file) => {
        comprobantesUrls.push({
          url: file.originalUrl,
          descripcion: file.descripcion || ''
        });
      });

      const pagoData = {
        monto: values.monto,
        metodo_pago_id: values.metodo_pago_id,
        comprobantes: comprobantesUrls.length > 0 ? comprobantesUrls : null,
        observaciones: values.observaciones || null,
        fecha_pago: values.fecha_pago ? values.fecha_pago.format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'),
        estado: values.estado || 'pendiente',
        metodo_pago_nombre: metodosPago.find(m => m.id === values.metodo_pago_id)?.nombre || ''
      };

      // Si el servicio ya está guardado, crear el pago inmediatamente
      if (editingServicio?.id) {
        pagoData.servicio_id = editingServicio.id;

        if (editingPago) {
          const response = await api.put(`/pagos-servicios/${editingPago.id}`, pagoData);
          message.success('Pago actualizado exitosamente');
          if (response.data?.cobro_warning) {
            message.warning(response.data.cobro_warning);
          } else if (response.data?.cobro_caja_chica_id) {
            message.info('Cobro sincronizado en la caja chica del técnico');
          }
          fetchPagosServicio(editingServicio.id);
        } else {
          const response = await api.post('/pagos-servicios', pagoData);
          message.success('Pago agregado exitosamente');
          if (response.data?.cobro_warning) {
            message.warning(response.data.cobro_warning);
          } else if (response.data?.cobro_caja_chica_id) {
            message.info('Se registró también el cobro en la caja chica del técnico');
          }
          fetchPagosServicio(editingServicio.id);
        }
      } else {
        // Si el servicio aún no está guardado, guardar temporalmente en pagosPendientes
        if (editingPago) {
          // Actualizar pago pendiente existente
          const updatedPagos = pagosPendientes.map(p =>
            p.tempId === editingPago.tempId ? { ...pagoData, tempId: editingPago.tempId } : p
          );
          setPagosPendientes(updatedPagos);
          message.success('Pago actualizado (se guardará al crear el servicio)');
        } else {
          // Agregar nuevo pago pendiente
          const nuevoPago = {
            ...pagoData,
            tempId: `temp-${Date.now()}-${Math.random()}`
          };
          setPagosPendientes([...pagosPendientes, nuevoPago]);
          message.success('Pago agregado (se guardará al crear el servicio)');
        }
      }

      setPagoModalVisible(false);
      pagoForm.resetFields();
      setComprobantesPagoForm([]);
      setEditingPago(null);
    } catch (error) {
      message.error(error.message);
    }
  };

  const buscarTarifaBase = async (aseguradoraId, tipoServicioId, forzarActualizacion = false) => {
    if (!aseguradoraId || !tipoServicioId) {
      return;
    }
    try {
      const response = await api.get(`/tarifas-base?aseguradora_id=${aseguradoraId}&tipo_servicio_id=${tipoServicioId}`);
      if (response.data && response.data.precio) {
        // Si se fuerza la actualización o no hay costo actual, actualizar el costo
        const costoActual = form.getFieldValue('costo');
        if (forzarActualizacion || !costoActual || costoActual === 0) {
          form.setFieldsValue({ costo: parseFloat(response.data.precio) });
        }
      } else {
        // Si no se encuentra tarifa base, limpiar el costo si se está forzando la actualización
        if (forzarActualizacion) {
          form.setFieldsValue({ costo: undefined });
        }
      }
    } catch (error) {
      // Si no se encuentra tarifa base, limpiar el costo si se está forzando la actualización
      if (forzarActualizacion) {
        form.setFieldsValue({ costo: undefined });
      }
      console.log('No se encontró tarifa base para esta combinación');
    }
  };

  const handleAsignarPersonal = async (servicioId, trabajadorId) => {
    try {
      await api.put(`/servicios/${servicioId}`, { trabajador_id: trabajadorId || null });
      message.success('Personal asignado exitosamente');
      fetchServicios();
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleCrearCliente = async (values) => {
    try {
      const response = await api.post('/clientes', values);
      message.success('Cliente creado exitosamente');
      setClienteModalVisible(false);
      clienteForm.resetFields();
      await fetchClientes();
      const clienteId = response.data.id || response.data;
      form.setFieldsValue({ cliente_id: clienteId.toString() });
    } catch (error) {
      message.error(error.message);
    }
  };

  const handleCrearVehiculo = async (values) => {
    try {
      const clienteId = form.getFieldValue('cliente_id');
      if (!clienteId) {
        message.warning('Primero selecciona un cliente');
        return;
      }
      const data = { ...values, cliente_id: clienteId };
      const response = await api.post('/vehiculos', data);
      message.success('Vehículo creado exitosamente');
      setVehiculoModalVisible(false);
      vehiculoForm.resetFields();
      await fetchVehiculos();
      const nuevoVehiculo = response.data;
      // El backend devuelve el objeto completo del vehículo
      const vehiculoData = nuevoVehiculo;
      const marca = vehiculoData.marca || values.marca || '';
      const modelo = vehiculoData.modelo || values.modelo || '';
      const tipoVehiculo = (marca && modelo) ? `${marca} ${modelo}` : (marca || modelo || '');

      form.setFieldsValue({
        vehiculo_id: vehiculoData.id,
        placa: vehiculoData.placa || values.placa,
        marca_vehiculo: vehiculoData.marca || values.marca,
        modelo_vehiculo: vehiculoData.modelo || values.modelo,
        anio_vehiculo: vehiculoData.anio || vehiculoData.año || values.anio || values.año,
        tipo_vehiculo: tipoVehiculo
      });
      // Agregar el nuevo vehículo a la lista
      if (vehiculoData.placa) {
        setVehiculos([...vehiculos, vehiculoData]);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const handlePlacaChange = (placa) => {
    if (!placa) {
      form.setFieldsValue({
        vehiculo_id: null,
        marca_vehiculo: null,
        modelo_vehiculo: null,
        anio_vehiculo: null,
        tipo_vehiculo: null
      });
      return;
    }

    const vehiculo = vehiculos.find(v => v.placa === placa);
    if (vehiculo) {
      const marca = vehiculo.marca || '';
      const modelo = vehiculo.modelo || '';
      const tipoVehiculo = (marca && modelo) ? `${marca} ${modelo}` : (marca || modelo || '');

      form.setFieldsValue({
        vehiculo_id: vehiculo.id,
        marca_vehiculo: vehiculo.marca,
        modelo_vehiculo: vehiculo.modelo,
        anio_vehiculo: vehiculo.anio || vehiculo.año,
        tipo_vehiculo: tipoVehiculo
      });
    } else {
      // Si la placa no existe, limpiar campos pero mantener la placa
      form.setFieldsValue({
        vehiculo_id: null,
        marca_vehiculo: null,
        modelo_vehiculo: null,
        anio_vehiculo: null,
        tipo_vehiculo: null
      });
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      // Verificar si la placa existe, si no, crear el vehículo automáticamente
      if (values.placa) {
        const placaNormalizada = values.placa.trim().toUpperCase();
        let vehiculoExistente = vehiculos.find(v => v.placa.toUpperCase() === placaNormalizada);

        if (!vehiculoExistente) {
          // Crear vehículo automáticamente con solo la placa
          try {
            const nuevoVehiculoData = {
              placa: placaNormalizada,
              cliente_id: values.cliente_id || null,
              marca: values.marca_vehiculo || null,
              modelo: values.modelo_vehiculo || null,
              anio: values.anio_vehiculo || null,
              color: null,
              kilometraje: null
            };

            const vehiculoResponse = await api.post('/vehiculos', nuevoVehiculoData);

            // Verificar que la respuesta sea exitosa y contenga el ID
            if (vehiculoResponse.data && vehiculoResponse.data.id) {
              vehiculoExistente = vehiculoResponse.data;

              // Actualizar la lista de vehículos
              await fetchVehiculos();

              // Actualizar el formulario con el nuevo vehículo
              values.vehiculo_id = vehiculoExistente.id;
              form.setFieldsValue({
                vehiculo_id: vehiculoExistente.id
              });

              message.success(`Vehículo con placa ${placaNormalizada} creado automáticamente`);
            } else {
              // Si no se recibió el ID directamente, esperar un momento y buscar en la lista actualizada
              await new Promise(resolve => setTimeout(resolve, 500));

              // Actualizar la lista de vehículos
              await fetchVehiculos();

              // Buscar el vehículo en la lista actualizada
              const vehiculosActualizados = await api.get('/vehiculos');
              if (vehiculosActualizados.data && Array.isArray(vehiculosActualizados.data)) {
                vehiculoExistente = vehiculosActualizados.data.find(v =>
                  v.placa && v.placa.toUpperCase() === placaNormalizada
                );

                if (vehiculoExistente && vehiculoExistente.id) {
                  values.vehiculo_id = vehiculoExistente.id;
                  form.setFieldsValue({
                    vehiculo_id: vehiculoExistente.id
                  });
                  message.success(`Vehículo con placa ${placaNormalizada} creado automáticamente`);
                } else {
                  throw new Error('No se pudo encontrar el vehículo creado en la lista');
                }
              } else {
                throw new Error('No se pudo obtener la lista de vehículos');
              }
            }
          } catch (error) {
            console.error('Error al crear vehículo automáticamente:', error);
            const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
            message.error(`Error al crear vehículo: ${errorMessage}. Por favor, crea el vehículo manualmente antes de continuar.`);
            setSubmitting(false);
            return; // Detener el proceso si no se puede crear el vehículo
          }
        } else {
          // Si el vehículo existe, asegurar que vehiculo_id esté asignado
          if (!values.vehiculo_id && vehiculoExistente.id) {
            values.vehiculo_id = vehiculoExistente.id;
            form.setFieldsValue({
              vehiculo_id: vehiculoExistente.id
            });
          }
        }
      }

      // Separar campos del servicio de los campos del checklist
      const checklistFields = [
        'tarjeta_propiedad', 'soat', 'cabeceras', 'llave_control', 'encendedor',
        'radio_mascarilla', 'parlantes', 'ceniceros', 'tapa_sol', 'tapa_radiador',
        'varilla_aceite', 'pisos', 'faros_delanteros', 'faros_posteriores', 'plumillas',
        'tapa_combustible', 'antena', 'espejo_interior', 'espejo_exterior', 'bateria',
        'tapa_aceite', 'extintor', 'triangulo_cono', 'seguro_rueda', 'gata',
        'llave_rueda', 'vasos_copas', 'aros', 'llanta_repuesto', 'tapa_liq_emb', 'emblema'
      ];

      const servicioData = {};
      const checklistData = {};

      // Separar los campos
      Object.keys(values).forEach(key => {
        if (checklistFields.includes(key)) {
          checklistData[key] = values[key];
        } else {
          servicioData[key] = values[key];
        }
      });

      // Subir imágenes y comprobantes en paralelo si es posible
      let imagenesUrls = [];
      let comprobantesAdelantoUrls = [];
      let comprobantesPagoUrls = [];

      // Preparar datos para subidas
      const formDataImagenes = new FormData();
      const existingImagenes = []; // Cambiar a array de objetos

      if (fileList.length > 0) {
        fileList.forEach((file) => {
          if (file.originFileObj) {
            formDataImagenes.append('imagenes[]', file.originFileObj);
          } else if (file.url || file.originalUrl) {
            // Si es una imagen existente, mantener su estructura con descripción
            const url = file.originalUrl || file.url;
            existingImagenes.push({
              url: url,
              descripcion: file.descripcion || ''
            });
          }
        });
      }

      // Comprobantes de adelanto (múltiples)
      const formDataComprobantesAdelanto = new FormData();
      const existingComprobantesAdelanto = [];
      let tieneComprobantesAdelantoNuevos = false;
      if (comprobanteAdelanto.length > 0) {
        comprobanteAdelanto.forEach((file) => {
          if (file.originFileObj) {
            formDataComprobantesAdelanto.append('imagenes[]', file.originFileObj);
            tieneComprobantesAdelantoNuevos = true;
          } else if (file.url || file.originalUrl) {
            const url = file.originalUrl || file.url;
            existingComprobantesAdelanto.push({
              url: url,
              descripcion: file.descripcion || ''
            });
          }
        });
      }

      // Comprobantes de pago (múltiples)
      const formDataComprobantesPago = new FormData();
      const existingComprobantesPago = [];
      let tieneComprobantesPagoNuevos = false;
      if (comprobantesPago.length > 0) {
        comprobantesPago.forEach((file) => {
          if (file.originFileObj) {
            formDataComprobantesPago.append('imagenes[]', file.originFileObj);
            tieneComprobantesPagoNuevos = true;
          } else if (file.url || file.originalUrl) {
            const url = file.originalUrl || file.url;
            existingComprobantesPago.push({
              url: url,
              descripcion: file.descripcion || ''
            });
          }
        });
      }

      // Realizar subidas en paralelo
      const uploadPromises = [];

      if (formDataImagenes.has('imagenes[]')) {
        uploadPromises.push(
          api.post('/servicios/upload-images', formDataImagenes, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }).then(response => {
            // Combinar URLs nuevas con descripciones del fileList
            const nuevasImagenes = response.data.urls.map((url, index) => {
              // Buscar el archivo correspondiente en fileList por índice
              const archivosNuevos = fileList.filter(f => f.originFileObj);
              const archivoCorrespondiente = archivosNuevos[index];
              return {
                url: url,
                descripcion: archivoCorrespondiente?.descripcion || ''
              };
            });
            imagenesUrls = [...existingImagenes, ...nuevasImagenes];
          }).catch(error => {
            console.error('Error al subir imágenes:', error);
            throw new Error('Error al subir imágenes');
          })
        );
      } else {
        imagenesUrls = existingImagenes;
      }

      if (tieneComprobantesAdelantoNuevos) {
        uploadPromises.push(
          api.post('/servicios/upload-images', formDataComprobantesAdelanto, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }).then(response => {
            // Combinar URLs nuevas con descripciones
            const nuevosComprobantes = response.data.urls.map((url, index) => {
              const archivosNuevos = comprobanteAdelanto.filter(f => f.originFileObj);
              const archivoCorrespondiente = archivosNuevos[index];
              return {
                url: url,
                descripcion: archivoCorrespondiente?.descripcion || ''
              };
            });
            comprobantesAdelantoUrls = [...existingComprobantesAdelanto, ...nuevosComprobantes];
          }).catch(error => {
            console.error('Error al subir comprobantes de adelanto:', error);
            throw new Error('Error al subir comprobantes de adelanto');
          })
        );
      } else {
        comprobantesAdelantoUrls = existingComprobantesAdelanto;
      }

      if (tieneComprobantesPagoNuevos) {
        uploadPromises.push(
          api.post('/servicios/upload-images', formDataComprobantesPago, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }).then(response => {
            // Combinar URLs nuevas con descripciones
            const nuevosComprobantes = response.data.urls.map((url, index) => {
              const archivosNuevos = comprobantesPago.filter(f => f.originFileObj);
              const archivoCorrespondiente = archivosNuevos[index];
              return {
                url: url,
                descripcion: archivoCorrespondiente?.descripcion || ''
              };
            });
            comprobantesPagoUrls = [...existingComprobantesPago, ...nuevosComprobantes];
          }).catch(error => {
            console.error('Error al subir comprobantes de pago:', error);
            throw new Error('Error al subir comprobantes de pago');
          })
        );
      } else {
        comprobantesPagoUrls = existingComprobantesPago;
      }

      // Esperar a que todas las subidas terminen
      if (uploadPromises.length > 0) {
        await Promise.all(uploadPromises);
      }

      // Formatear fecha_solicitud si es extemporáneo
      if (esExtemporaneo && values.fecha_solicitud) {
        servicioData.fecha_solicitud = dayjs(values.fecha_solicitud).format('YYYY-MM-DD HH:mm:ss');
      }

      // Guardar campos de ubicación por separado
      // Los nombres de departamento, provincia y distrito se guardan como IDs (códigos)
      if (values.departamento_contacto) {
        servicioData.departamento_contacto = values.departamento_contacto;
      }
      if (values.provincia_contacto) {
        servicioData.provincia_contacto = values.provincia_contacto;
      }
      if (values.distrito_contacto) {
        servicioData.distrito_contacto = values.distrito_contacto;
      }
      if (values.departamento_destino) {
        servicioData.departamento_destino = values.departamento_destino;
      }
      if (values.provincia_destino) {
        servicioData.provincia_destino = values.provincia_destino;
      }
      if (values.distrito_destino) {
        servicioData.distrito_destino = values.distrito_destino;
      }

      const data = {
        ...servicioData,
        checklist: checklistData,
        imagenes: imagenesUrls,
        fallas_mecanicas: values.fallas_mecanicas || [],
        comprobantes_adelanto: comprobantesAdelantoUrls.length > 0 ? comprobantesAdelantoUrls : null,
        comprobantes_pago: comprobantesPagoUrls.length > 0 ? comprobantesPagoUrls : null,
        incluye_igv: servicioData.incluye_igv !== undefined ? servicioData.incluye_igv : true,
        copago: servicioData.copago || 0,
        comision: servicioData.comision || 0,
        estado_pago: servicioData.estado_pago || 'validacion'
      };

      let servicioId;
      if (editingServicio) {
        await api.put(`/servicios/${editingServicio.id}`, data);
        servicioId = editingServicio.id;
      } else {
        const response = await api.post('/servicios', data);
        servicioId = response.data?.id || response.data?.servicio_id;

        // Si hay pagos pendientes, crearlos ahora que el servicio tiene ID
        if (pagosPendientes.length > 0 && servicioId) {
          try {
            const pagosPromises = pagosPendientes.map(pago => {
              const pagoData = {
                servicio_id: servicioId,
                monto: pago.monto,
                metodo_pago_id: pago.metodo_pago_id,
                comprobantes: pago.comprobantes,
                observaciones: pago.observaciones,
                fecha_pago: pago.fecha_pago,
                estado: pago.estado
              };
              return api.post('/pagos-servicios', pagoData);
            });
            const pagosResponses = await Promise.all(pagosPromises);
            message.info(`${pagosPendientes.length} pago(s) agregado(s) al servicio`);
            pagosResponses.forEach((response) => {
              if (response.data?.cobro_warning) {
                message.warning(response.data.cobro_warning);
              }
            });
          } catch (error) {
            console.error('Error al crear pagos pendientes:', error);
            message.warning('Servicio creado pero algunos pagos no se pudieron guardar');
          }
        }
      }

      setModalVisible(false);
      form.resetFields();
      setEditingServicio(null);
      setFileList([]);
      setComprobanteAdelanto([]);
      setComprobantesPago([]);
      setPagosServicio([]);
      setPagosPendientes([]);
      setEsExtemporaneo(false);
      // Limpiar estados de ubicación
      setDepartamentoContacto(null);
      setProvinciaContacto(null);
      setDistritoContacto(null);
      setDepartamentoDestino(null);
      setProvinciaDestino(null);
      setDistritoDestino(null);

      // Recargar servicios en segundo plano sin bloquear la UI
      fetchServicios().catch(err => {
        console.error('Error al recargar servicios:', err);
      });

      message.success(editingServicio ? 'Servicio actualizado exitosamente' : 'Servicio creado exitosamente');
    } catch (error) {
      console.error('Error al guardar servicio:', error);
      const errorMessage = error.message || 'Error al guardar servicio';
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (record) => {
    // Actualizar asistencias antes de abrir el modal
    await fetchAsistencias();
    setEditingServicio(record);
    setEsExtemporaneo(false); // Resetear checkbox al editar
    // Solo activar modo solo lectura si está completado Y no es administrador
    const esAdmin = user && user.rol === 'admin';
    setModoSoloLectura(record.estado === 'completado' && !esAdmin);
    const formValues = {
      ...record,
      // Mantener como números para que los Select los reconozcan correctamente
      cliente_id: record.cliente_id ? Number(record.cliente_id) : undefined,
      vehiculo_id: record.vehiculo_id ? Number(record.vehiculo_id) : undefined,
      tipo_servicio_id: record.tipo_servicio_id ? Number(record.tipo_servicio_id) : undefined,
      aseguradora_id: record.aseguradora_id ? Number(record.aseguradora_id) : undefined,
      empresa_id: record.empresa_id ? Number(record.empresa_id) : undefined,
      trabajador_id: record.trabajador_id ? Number(record.trabajador_id) : undefined,
      unidad_id: record.unidad_id ? Number(record.unidad_id) : undefined,
      metodo_pago_id: record.metodo_pago_id ? Number(record.metodo_pago_id) : undefined,
      metodo_pago_adelanto_id: record.metodo_pago_adelanto_id ? Number(record.metodo_pago_adelanto_id) : undefined,
      // Convertir año_vehiculo a anio_vehiculo si existe
      anio_vehiculo: record.anio_vehiculo || record.año_vehiculo,
    };

    // Eliminar año_vehiculo si existe para evitar conflictos
    if (formValues.año_vehiculo !== undefined) {
      delete formValues.año_vehiculo;
    }

    if (record.checklist) {
      Object.keys(record.checklist).forEach(key => {
        if (key !== 'id' && key !== 'servicio_id') {
          formValues[key] = record.checklist[key];
        }
      });
    }

    // Cargar imágenes existentes
    if (record.imagenes && Array.isArray(record.imagenes) && record.imagenes.length > 0) {
      const existingFiles = record.imagenes.map((imagen, index) => {
        // Si es un objeto con url y descripcion
        const url = typeof imagen === 'object' && imagen.url ? imagen.url : imagen;
        const descripcion = typeof imagen === 'object' && imagen.descripcion ? imagen.descripcion : '';
        return {
          uid: `existing-${index}`,
          name: `imagen-${index + 1}.jpg`,
          status: 'done',
          url: getImageUrl(url),
          thumbUrl: getImageUrl(url),
          descripcion: descripcion,
          originalUrl: url, // Guardar la URL original para referencia
        };
      });
      setFileList(existingFiles);
    } else {
      setFileList([]);
    }

    // Cargar comprobantes de adelanto si existen
    if (record.comprobantes_adelanto && Array.isArray(record.comprobantes_adelanto) && record.comprobantes_adelanto.length > 0) {
      const comprobantesFiles = record.comprobantes_adelanto.map((img, index) => {
        const url = typeof img === 'object' && img.url ? img.url : img;
        return {
          uid: `comprobante-adelanto-${index}`,
          name: `comprobante-adelanto-${index}.jpg`,
          status: 'done',
          url: getImageUrl(url),
          thumbUrl: getImageUrl(url),
          originalUrl: url,
          descripcion: typeof img === 'object' && img.descripcion ? img.descripcion : ''
        };
      });
      setComprobanteAdelanto(comprobantesFiles);
    } else if (record.comprobante_adelanto) {
      // Compatibilidad con formato antiguo (string)
      setComprobanteAdelanto([{
        uid: 'comprobante-adelanto',
        name: 'comprobante-adelanto.jpg',
        status: 'done',
        url: getImageUrl(record.comprobante_adelanto),
        thumbUrl: getImageUrl(record.comprobante_adelanto),
        originalUrl: record.comprobante_adelanto,
      }]);
    } else {
      setComprobanteAdelanto([]);
    }

    // Cargar comprobantes de pago si existen
    if (record.comprobantes_pago && Array.isArray(record.comprobantes_pago) && record.comprobantes_pago.length > 0) {
      const comprobantesPagoFiles = record.comprobantes_pago.map((img, index) => {
        const url = typeof img === 'object' && img.url ? img.url : img;
        return {
          uid: `comprobante-pago-${index}`,
          name: `comprobante-pago-${index}.jpg`,
          status: 'done',
          url: getImageUrl(url),
          thumbUrl: getImageUrl(url),
          originalUrl: url,
          descripcion: typeof img === 'object' && img.descripcion ? img.descripcion : ''
        };
      });
      setComprobantesPago(comprobantesPagoFiles);
    } else {
      setComprobantesPago([]);
    }

    // Cargar fallas mecánicas si existen
    if (record.fallas_mecanicas && Array.isArray(record.fallas_mecanicas)) {
      formValues.fallas_mecanicas = record.fallas_mecanicas;
    } else {
      formValues.fallas_mecanicas = [];
    }

    // Cargar nuevos campos de pago
    formValues.incluye_igv = record.incluye_igv !== undefined ? record.incluye_igv : true;
    formValues.copago = record.copago || 0;
    formValues.comision = record.comision || 0;
    formValues.estado_pago = record.estado_pago || 'validacion';

    // Cargar ubicación de contacto desde campos separados
    if (record.departamento_contacto) {
      formValues.departamento_contacto = record.departamento_contacto;
      setDepartamentoContacto(record.departamento_contacto);
    } else {
      setDepartamentoContacto(null);
    }
    if (record.provincia_contacto) {
      formValues.provincia_contacto = record.provincia_contacto;
      setProvinciaContacto(record.provincia_contacto);
    } else {
      setProvinciaContacto(null);
    }
    if (record.distrito_contacto) {
      formValues.distrito_contacto = record.distrito_contacto;
      setDistritoContacto(record.distrito_contacto);
    } else {
      setDistritoContacto(null);
    }

    // Cargar ubicación de destino desde campos separados
    if (record.departamento_destino) {
      formValues.departamento_destino = record.departamento_destino;
      setDepartamentoDestino(record.departamento_destino);
    } else {
      setDepartamentoDestino(null);
    }
    if (record.provincia_destino) {
      formValues.provincia_destino = record.provincia_destino;
      setProvinciaDestino(record.provincia_destino);
    } else {
      setProvinciaDestino(null);
    }
    if (record.distrito_destino) {
      formValues.distrito_destino = record.distrito_destino;
      setDistritoDestino(record.distrito_destino);
    } else {
      setDistritoDestino(null);
    }

    // Cargar pagos del servicio
    if (record.id) {
      fetchPagosServicio(record.id);
      // Cargar seguimiento del servicio
      fetchSeguimiento(record.id);
    } else {
      setPagosServicio([]);
      setSeguimientoMensajes([]);
    }

    setModalVisible(true);
    setActiveTab('1'); // Resetear a la primera pestaña

    // Usar setTimeout para asegurar que el modal esté completamente renderizado y las opciones estén disponibles
    setTimeout(() => {
      form.setFieldsValue(formValues);
    }, 100);
  };

  const fetchSeguimiento = async (servicioId) => {
    setCargandoSeguimiento(true);
    try {
      const response = await api.get(`/seguimiento?servicio_id=${servicioId}`);
      setSeguimientoMensajes(response.data || []);
    } catch (error) {
      console.error('Error al cargar seguimiento:', error);
      setSeguimientoMensajes([]);
    } finally {
      setCargandoSeguimiento(false);
    }
  };

  const handleEnviarMensaje = async () => {
    if (!editingServicio?.id) return;

    if (!nuevoMensaje.trim() && !imagenSeguimiento) {
      message.warning('Ingresa un mensaje o adjunta una imagen');
      return;
    }

    try {
      let imagenUrl = null;

      // Si hay imagen, subirla primero
      if (imagenSeguimiento) {
        const formData = new FormData();
        formData.append('imagenes', imagenSeguimiento);
        const uploadResponse = await api.post('/servicios/upload-images', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        imagenUrl = uploadResponse.data.urls[0];
      }

      // Enviar mensaje
      const response = await api.post('/seguimiento', {
        servicio_id: editingServicio.id,
        mensaje: nuevoMensaje.trim() || null,
        imagen: imagenUrl
      });

      // Agregar el nuevo mensaje a la lista
      setSeguimientoMensajes([...seguimientoMensajes, response.data]);
      setNuevoMensaje('');
      setImagenSeguimiento(null);
      message.success('Mensaje enviado');
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      message.error(error.message);
    }
  };

  const handleEliminarMensaje = async (mensajeId) => {
    try {
      await api.delete(`/seguimiento/${mensajeId}`);
      setSeguimientoMensajes(seguimientoMensajes.filter(m => m.id !== mensajeId));
      message.success('Mensaje eliminado');
    } catch (error) {
      console.error('Error al eliminar mensaje:', error);
      message.error(error.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/servicios/${id}`);
      message.success('Servicio eliminado exitosamente');
      fetchServicios();
    } catch (error) {
      message.error(error.message);
    }
  };

  const checklistItems = [
    { key: 'tarjeta_propiedad', label: 'Tarjeta de Propiedad' },
    { key: 'soat', label: 'SOAT' },
    { key: 'cabeceras', label: 'Cabeceras' },
    { key: 'llave_control', label: 'Llave - Control/R' },
    { key: 'encendedor', label: 'Encendedor' },
    { key: 'radio_mascarilla', label: 'Radio - Mascarilla' },
    { key: 'parlantes', label: 'Parlantes' },
    { key: 'ceniceros', label: 'Ceniceros' },
    { key: 'tapa_sol', label: 'Tapa sol' },
    { key: 'tapa_radiador', label: 'Tapa de Radiador' },
    { key: 'varilla_aceite', label: 'Varilla de Aceite' },
    { key: 'pisos', label: 'Pisos' },
    { key: 'faros_delanteros', label: 'Faros Delanteros' },
    { key: 'faros_posteriores', label: 'Faros Posteriores' },
    { key: 'plumillas', label: 'Plumillas' },
    { key: 'tapa_combustible', label: 'Tapa Combustible' },
    { key: 'antena', label: 'Antena' },
    { key: 'espejo_interior', label: 'Espejo Interior' },
    { key: 'espejo_exterior', label: 'Espejo Exterior' },
    { key: 'bateria', label: 'Bateria' },
    { key: 'tapa_aceite', label: 'Tapa de Aceite' },
    { key: 'extintor', label: 'Extintor' },
    { key: 'triangulo_cono', label: 'Triangulo / Cono' },
    { key: 'seguro_rueda', label: 'Seguro de Rueda' },
    { key: 'gata', label: 'Gata' },
    { key: 'llave_rueda', label: 'Llave / Rueda' },
    { key: 'vasos_copas', label: 'Vasos / Copas' },
    { key: 'aros', label: 'Aros' },
    { key: 'llanta_repuesto', label: 'Llanta / Repuesto' },
    { key: 'tapa_liq_emb', label: 'Tapa de Liq. de Emb' },
    { key: 'emblema', label: 'Emblema' },
  ];

  const columns = [
    {
      title: 'Fecha Solicitud',
      dataIndex: 'fecha_solicitud',
      key: 'fecha_solicitud',
      width: 150,
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Expediente',
      dataIndex: 'numero_expediente',
      key: 'numero_expediente',
      width: 120,
      render: (expediente) => expediente || '-',
    },
    {
      title: 'Cliente',
      key: 'cliente',
      render: (_, record) =>
        `${record.cliente_nombre || ''} ${record.cliente_apellido || ''}`,
    },
    {
      title: 'Placa',
      dataIndex: 'placa',
      key: 'placa',
    },
    {
      title: 'Tipo de Servicio',
      dataIndex: 'tipo_servicio_nombre',
      key: 'tipo_servicio',
    },
    {
      title: 'Aseguradora',
      dataIndex: 'aseguradora_nombre',
      key: 'aseguradora',
    },
    ...(isMainAdmin ? [{
      title: 'Empresa',
      dataIndex: 'empresa_nombre',
      key: 'empresa',
      render: (empresa) => empresa || <Tag color="default">Sin empresa</Tag>,
    }] : []),
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        const colors = {
          pendiente: 'orange',
          en_proceso: 'blue',
          completado: 'green',
          cancelado: 'red',
        };
        const labels = {
          pendiente: 'PENDIENTE',
          en_proceso: 'EN PROCESO',
          completado: 'COMPLETADO',
          cancelado: 'ANULADO',
        };
        return <Tag color={colors[estado] || 'default'}>{labels[estado] || estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Estado de Pago',
      dataIndex: 'estado_pago',
      key: 'estado_pago',
      render: (estadoPago) => {
        const colors = {
          validacion: 'orange',
          pagado: 'green',
        };
        const labels = {
          validacion: 'VALIDACIÓN',
          pagado: 'PAGADO',
        };
        const estado = estadoPago || 'validacion';
        return <Tag color={colors[estado] || 'default'}>{labels[estado] || estado.toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Personal Asignado',
      key: 'trabajador',
      width: 200,
      render: (_, record) => (
        record.trabajador_nombre ? (
          <span>{record.trabajador_nombre}</span>
        ) : (
          <span style={{ color: '#999' }}>Sin asignar</span>
        )
      ),
    },
    {
      title: 'Acciones',
      key: 'acciones',
      fixed: 'right',
      width: 150,
      render: (_, record) => {
        const esCompletado = record.estado === 'completado';
        const esAdmin = user && user.rol === 'admin';
        // Los administradores pueden editar servicios completados
        const puedeEditar = !esCompletado || esAdmin;
        return (
          <Space size="small" wrap>
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              size="small"
              disabled={!puedeEditar}
            >
              <span className="hide-on-mobile">{esCompletado && !esAdmin ? 'Ver' : 'Editar'}</span>
            </Button>
            {(!esCompletado || esAdmin) && (
              <Popconfirm
                title="¿Estás seguro de eliminar este servicio?"
                onConfirm={() => handleDelete(record.id)}
                okText="Sí"
                cancelText="No"
              >
                <Button type="primary" danger icon={<DeleteOutlined />} size="small">
                  <span className="hide-on-mobile">Eliminar</span>
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Card
        title="Gestión de Servicios"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={async () => {
              // Actualizar asistencias antes de abrir el modal
              await fetchAsistencias();
              setEditingServicio(null);
              form.resetFields();
              setFileList([]);
              setComprobanteAdelanto([]);
              setEsExtemporaneo(false);
              setModoSoloLectura(false);
              // Limpiar estados de ubicación
              setDepartamentoContacto(null);
              setProvinciaContacto(null);
              setDistritoContacto(null);
              setDepartamentoDestino(null);
              setProvinciaDestino(null);
              setDistritoDestino(null);
              setModalVisible(true);
            }}
          >
            <span className="hide-on-mobile">Nuevo Servicio</span>
            <span className="show-on-mobile">Nuevo</span>
          </Button>
        }
      >
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Input.Search
                placeholder="Buscar por expediente o cliente"
                allowClear
                enterButton
                size="large"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  if (!e.target.value) {
                    fetchServicios('');
                  }
                }}
                onSearch={(value) => {
                  fetchServicios(value);
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Select
                placeholder="Filtrar por Técnico"
                allowClear
                size="large"
                style={{ width: '100%' }}
                value={filtroTecnico}
                onChange={(value) => {
                  setFiltroTecnico(value);
                  setTimeout(() => fetchServicios(searchText), 100);
                }}
              >
                {personal.map((p) => (
                  <Select.Option key={p.id} value={p.id}>
                    {p.nombre_completo}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Select
                placeholder="Filtrar por Unidad"
                allowClear
                size="large"
                style={{ width: '100%' }}
                value={filtroUnidad}
                onChange={(value) => {
                  setFiltroUnidad(value);
                  setTimeout(() => fetchServicios(searchText), 100);
                }}
              >
                {unidades.map((u) => (
                  <Select.Option key={u.id} value={u.id}>
                    {u.placa} - {u.tipo_grua}
                  </Select.Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={5}>
              <DatePicker.RangePicker
                size="large"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                placeholder={['Fecha Inicio', 'Fecha Fin']}
                onChange={(dates) => {
                  if (dates && dates.length === 2) {
                    setFiltroFechaInicio(dates[0]);
                    setFiltroFechaFin(dates[1]);
                    setTimeout(() => fetchServicios(searchText), 100);
                  } else {
                    setFiltroFechaInicio(null);
                    setFiltroFechaFin(null);
                    setTimeout(() => fetchServicios(searchText), 100);
                  }
                }}
                value={filtroFechaInicio && filtroFechaFin ? [dayjs(filtroFechaInicio), dayjs(filtroFechaFin)] : null}
              />
            </Col>
            <Col xs={24} sm={24} md={24} lg={5}>
              <Button
                size="large"
                onClick={() => {
                  setFiltroTecnico(null);
                  setFiltroUnidad(null);
                  setFiltroFechaInicio(null);
                  setFiltroFechaFin(null);
                  setSearchText('');
                  fetchServicios('');
                }}
                style={{ width: '100%' }}
              >
                Limpiar Filtros
              </Button>
            </Col>
          </Row>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={servicios}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </Card>

      <Modal
        title={editingServicio ? (modoSoloLectura ? 'Ver Servicio' : 'Editar Servicio') : 'Nuevo Servicio'}
        open={modalVisible}
        width="95%"
        style={{ maxWidth: 1200 }}
        bodyStyle={{ maxHeight: '90vh', overflowY: 'auto' }}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
          setEditingServicio(null);
          setFileList([]);
          setComprobanteAdelanto([]);
          setEsExtemporaneo(false);
          setModoSoloLectura(false);
          setSeguimientoMensajes([]);
          setNuevoMensaje('');
          setImagenSeguimiento(null);
          setActiveTab('1');
          // Limpiar estados de ubicación
          setDepartamentoContacto(null);
          setProvinciaContacto(null);
          setDistritoContacto(null);
          setDepartamentoDestino(null);
          setProvinciaDestino(null);
          setDistritoDestino(null);
        }}
        footer={null}
        width="95%"
        style={{ maxWidth: 1200, top: 20 }}
        bodyStyle={{ maxHeight: '90vh', overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column' }}
      >
        <Form form={form} layout="vertical" onFinish={modoSoloLectura ? undefined : handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ position: 'sticky', top: 0, zIndex: 10, background: '#fff', marginBottom: 16, paddingBottom: 8, borderBottom: '1px solid #f0f0f0' }}>
            <Tabs
              activeKey={activeTab}
              onChange={(key) => {
                setActiveTab(key);
                // Si se cambia a la pestaña de seguimiento, cargar los mensajes
                if (key === '4' && editingServicio?.id) {
                  fetchSeguimiento(editingServicio.id);
                }
              }}
              type="card"
              style={{ marginBottom: 0 }}
            >
              {/* Pestaña 1: Detalles del Servicio */}
              <Tabs.TabPane tab="Detalles del Servicio" key="1">
                <div style={{ paddingTop: 16, maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>

                  {/* SECCIÓN: Cliente y Servicio */}
                  <Card title="Cliente y Tipo de Servicio" size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="cliente_id"
                          label="Usuario/Cliente"
                          rules={[{ required: true, message: 'Selecciona un cliente' }]}
                        >
                          <Select
                            disabled={modoSoloLectura}
                            placeholder="Selecciona un cliente"
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                              const children = option?.children;
                              const text = Array.isArray(children) ? children.join('') : String(children ?? '');
                              return text.toLowerCase().includes(input.toLowerCase());
                            }}
                            dropdownRender={(menu) => (
                              <>
                                {menu}
                                <Divider style={{ margin: '8px 0' }} />
                                <Button type="link" icon={<PlusOutlined />} onClick={() => setClienteModalVisible(true)} style={{ width: '100%' }}>
                                  Crear nuevo cliente
                                </Button>
                              </>
                            )}
                          >
                            {opcionesClientesCompletas.map((c) => (
                              <Select.Option key={c.id} value={Number(c.id)}>{`${c.nombre} ${c.apellido}`}</Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          name="tipo_servicio_id"
                          label="Tipo de Servicio"
                          rules={[{ required: true, message: 'Selecciona un tipo de servicio' }]}
                        >
                          <Select
                            placeholder="Selecciona el tipo de servicio"
                            disabled={modoSoloLectura}
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                              const children = option?.children;
                              const text = Array.isArray(children) ? children.join('') : String(children ?? '');
                              return text.toLowerCase().includes(input.toLowerCase());
                            }}
                            onChange={(value) => {
                              const aseguradoraId = form.getFieldValue('aseguradora_id');
                              if (aseguradoraId) buscarTarifaBase(aseguradoraId, value);
                            }}
                          >
                            {tiposServicio.map((t) => (
                              <Select.Option key={t.id} value={t.id}>{t.nombre}</Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={isMainAdmin ? 12 : 24}>
                        <Form.Item name="aseguradora_id" label="Ordenante/Aseguradora">
                          <Select
                            placeholder="Selecciona una aseguradora"
                            disabled={modoSoloLectura}
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            filterOption={(input, option) => {
                              const children = option?.children;
                              const text = Array.isArray(children) ? children.join('') : String(children ?? '');
                              return text.toLowerCase().includes(input.toLowerCase());
                            }}
                            onChange={(value) => {
                              const tipoServicioId = form.getFieldValue('tipo_servicio_id');
                              if (value && tipoServicioId) {
                                buscarTarifaBase(value, tipoServicioId);
                              } else if (!value) {
                                form.setFieldsValue({ costo: undefined });
                              }
                            }}
                          >
                            {aseguradoras.map((a) => (
                              <Select.Option key={a.id} value={a.id}>{a.nombre}</Select.Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </Col>
                      {isMainAdmin && (
                        <Col span={12}>
                          <Form.Item name="empresa_id" label="Empresa" tooltip="Asignar este servicio a una empresa específica">
                            <Select
                              disabled={modoSoloLectura}
                              placeholder="Selecciona la empresa (opcional)"
                              allowClear
                              showSearch
                              filterOption={(input, option) => {
                                const children = option?.children;
                                const text = Array.isArray(children) ? children.join('') : String(children ?? '');
                                return text.toLowerCase().includes(input.toLowerCase());
                              }}
                            >
                              {empresas.map((e) => (
                                <Select.Option key={e.id} value={e.id}>{e.nombre}</Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      )}
                    </Row>
                  </Card>

                  {/* SECCIÓN: Información General y Vehículo */}
                  <Card title="Información General y Vehículo" size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item name="numero_expediente" label="Número de Expediente">
                          <Input placeholder="Ingresa el número de expediente (opcional)" disabled={modoSoloLectura} />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item name="extemporaneo" valuePropName="checked" style={{ paddingTop: 30 }}>
                          <Checkbox
                            disabled={modoSoloLectura}
                            onChange={(e) => {
                              setEsExtemporaneo(e.target.checked);
                              if (!e.target.checked) {
                                form.setFieldsValue({ fecha_solicitud: undefined, trabajador_id: undefined });
                              }
                            }}
                          >
                            Servicio Extemporáneo
                          </Checkbox>
                        </Form.Item>
                      </Col>
                    </Row>

                    {esExtemporaneo && (
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="fecha_solicitud"
                            label="Fecha del Servicio"
                            rules={[{ required: true, message: 'Selecciona la fecha del servicio' }]}
                          >
                            <DatePicker
                              style={{ width: '100%' }}
                              format="DD/MM/YYYY"
                              placeholder="Selecciona la fecha"
                              disabled={modoSoloLectura}
                              disabledDate={(current) => current && current > dayjs().endOf('day')}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    )}

                    <Divider dashed style={{ margin: '12px 0' }} />

                    <Row gutter={16}>
                      <Col span={8}>
                        <Form.Item
                          name="placa"
                          label="Placa de Vehículo"
                          rules={[{ required: true, message: 'Ingresa la placa' }]}
                        >
                          <AutoComplete
                            placeholder="Buscar Placa"
                            allowClear
                            onChange={handlePlacaChange}
                            onSelect={handlePlacaChange}
                            filterOption={(inputValue, option) =>
                              option?.value?.toUpperCase().includes(inputValue.toUpperCase())
                            }
                            options={vehiculos.map((vehiculo) => ({ value: vehiculo.placa, label: vehiculo.placa }))}
                            dropdownRender={(menu) => (
                              <>
                                {menu}
                                <Divider style={{ margin: '8px 0' }} />
                                <Button
                                  type="link"
                                  icon={<PlusOutlined />}
                                  onClick={() => setVehiculoModalVisible(true)}
                                  style={{ width: '100%' }}
                                >
                                  Registrar nuevo vehículo
                                </Button>
                              </>
                            )}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name="marca_vehiculo" label="Marca">
                          <Input disabled placeholder="Auto." />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name="modelo_vehiculo" label="Modelo">
                          <Input disabled placeholder="Auto." />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name="tipo_vehiculo" label="Tipo">
                          <Input disabled placeholder="Auto." />
                        </Form.Item>
                      </Col>
                      <Col span={4}>
                        <Form.Item name="anio_vehiculo" label="Año">
                          <InputNumber disabled style={{ width: '100%' }} placeholder="Auto." />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>

                  {/* SECCIÓN: Ubicación */}
                  <Card title="Ubicación" size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={24}>
                      {/* Lado izquierdo: CONTACTO/ORIGEN */}
                      <Col span={12} style={{ borderRight: '1px solid #f0f0f0' }}>
                        <Divider orientation="left" plain>Origen / Contacto</Divider>
                        <Row gutter={8}>
                          <Col span={24}>
                            <Form.Item name="departamento_contacto" label="Departamento">
                              <Select
                                placeholder="Departamento"
                                disabled={modoSoloLectura}
                                onChange={(value) => {
                                  setDepartamentoContacto(value);
                                  setProvinciaContacto(null);
                                  setDistritoContacto(null);
                                  form.setFieldsValue({ provincia_contacto: undefined, distrito_contacto: undefined });
                                }}
                                allowClear
                                showSearch
                                filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                              >
                                {departamentos.map((d) => <Select.Option key={d.id} value={d.id}>{d.nombre}</Select.Option>)}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item name="provincia_contacto" label="Provincia">
                              <Select
                                placeholder="Provincia"
                                disabled={modoSoloLectura || !departamentoContacto}
                                onChange={(value) => {
                                  setProvinciaContacto(value);
                                  setDistritoContacto(null);
                                  form.setFieldsValue({ distrito_contacto: undefined });
                                }}
                                allowClear
                                showSearch
                                filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                              >
                                {departamentoContacto && getProvinciasByDepartamento(departamentoContacto).map((p) => (
                                  <Select.Option key={p.id} value={p.id}>{p.nombre}</Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item name="distrito_contacto" label="Distrito">
                              <Select
                                placeholder="Distrito"
                                disabled={modoSoloLectura || !provinciaContacto}
                                onChange={setDistritoContacto}
                                allowClear
                                showSearch
                                filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                              >
                                {provinciaContacto && getDistritosByProvincia(provinciaContacto).map((d) => (
                                  <Select.Option key={d.id} value={d.id}>{d.nombre}</Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item name="lugar_contacto" label="Dirección / Referencia">
                              <Input disabled={modoSoloLectura} placeholder="Av. Principal 123..." />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>

                      {/* Lado derecho: DESTINO */}
                      <Col span={12}>
                        <Divider orientation="left" plain>Destino</Divider>
                        <Row gutter={8}>
                          <Col span={24}>
                            <Form.Item name="departamento_destino" label="Departamento">
                              <Select
                                placeholder="Departamento"
                                disabled={modoSoloLectura}
                                onChange={(value) => {
                                  setDepartamentoDestino(value);
                                  setProvinciaDestino(null);
                                  setDistritoDestino(null);
                                  form.setFieldsValue({ provincia_destino: undefined, distrito_destino: undefined });
                                }}
                                allowClear
                                showSearch
                                filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                              >
                                {departamentos.map((d) => <Select.Option key={d.id} value={d.id}>{d.nombre}</Select.Option>)}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item name="provincia_destino" label="Provincia">
                              <Select
                                placeholder="Provincia"
                                disabled={modoSoloLectura || !departamentoDestino}
                                onChange={(value) => {
                                  setProvinciaDestino(value);
                                  setDistritoDestino(null);
                                  form.setFieldsValue({ distrito_destino: undefined });
                                }}
                                allowClear
                                showSearch
                                filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                              >
                                {departamentoDestino && getProvinciasByDepartamento(departamentoDestino).map((p) => (
                                  <Select.Option key={p.id} value={p.id}>{p.nombre}</Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item name="distrito_destino" label="Distrito">
                              <Select
                                placeholder="Distrito"
                                disabled={modoSoloLectura || !provinciaDestino}
                                onChange={setDistritoDestino}
                                allowClear
                                showSearch
                                filterOption={(input, option) => option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0}
                              >
                                {provinciaDestino && getDistritosByProvincia(provinciaDestino).map((d) => (
                                  <Select.Option key={d.id} value={d.id}>{d.nombre}</Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={24}>
                            <Form.Item name="lugar_destino" label="Dirección / Referencia">
                              <Input disabled={modoSoloLectura} placeholder="Av. Destino 456..." />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Card>

                  {/* SECCIÓN: Asignación y Observaciones */}
                  <Card title="Asignación, Costos y Observaciones" size="small" style={{ marginBottom: 16 }}>
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          name="trabajador_id"
                          label="Asignar Operario"
                          tooltip={esExtemporaneo ? "Cualquier personal activo" : "Solo operarios con asistencia activa"}
                        >
                          <Select
                            disabled={modoSoloLectura}
                            placeholder="Selecciona el personal"
                            allowClear
                            showSearch
                            optionFilterProp="children"
                            onChange={async (value) => {
                              if (value) {
                                try {
                                  const asistenciaResponse = await api.get(`/asistencias?personal_id=${value}&unidad=1`);
                                  if (asistenciaResponse.data && asistenciaResponse.data.unidad_id) {
                                    const unidadId = asistenciaResponse.data.unidad_id;
                                    let kmActual = null;
                                    if (asistenciaResponse.data.km_actual != null) {
                                      kmActual = parseFloat(asistenciaResponse.data.km_actual);
                                    } else {
                                      try {
                                        const unidadResponse = await api.get(`/unidades/${unidadId}`);
                                        if (unidadResponse.data && unidadResponse.data.km_actual != null) {
                                          kmActual = parseFloat(unidadResponse.data.km_actual);
                                        }
                                      } catch (error) { console.error(error); }
                                    }
                                    const valuesToSet = { unidad_id: unidadId };
                                    if (kmActual != null) valuesToSet.km_inicio = kmActual;
                                    form.setFieldsValue(valuesToSet);
                                    setTimeout(() => {
                                      if (kmActual !== null && form.getFieldValue('km_inicio') !== kmActual) {
                                        form.setFieldsValue({ km_inicio: kmActual });
                                      }
                                    }, 100);
                                  }
                                } catch (error) { console.error('Error al obtener unidad:', error); }
                              } else {
                                form.setFieldsValue({ km_inicio: null, unidad_id: null });
                              }
                            }}
                            filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
                          >
                            {opcionesPersonalCompletas.map((p) => <Select.Option key={p.id} value={p.id}>{`${p.nombre_completo} - ${p.cargo}`}</Select.Option>)}
                          </Select>
                          {!esExtemporaneo && personalDisponible.length === 0 && (
                            <div style={{ color: '#ff4d4f', fontSize: '12px' }}>No hay operarios con asistencia activa.</div>
                          )}
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="km_inicio" label="Km. Inicio">
                          <InputNumber disabled={modoSoloLectura} min={0} precision={2} style={{ width: '100%' }} placeholder="Auto" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="km_fin" label="Km. Fin">
                          <InputNumber disabled={modoSoloLectura} min={0} precision={2} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      <Col span={24}>
                        <Form.Item name="observaciones" label="Observaciones">
                          <Input.TextArea disabled={modoSoloLectura} rows={3} />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Card>
                </div>
              </Tabs.TabPane>

              {/* Pestaña 2: Inventario */}
              <Tabs.TabPane tab="Inventario" key="2">
                <div style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
                  <Divider>Imágenes del Servicio</Divider>
                  <Form.Item>
                    <Upload
                      disabled={modoSoloLectura}
                      name="imagenes"
                      multiple
                      fileList={fileList}
                      onChange={({ fileList: newFileList }) => {
                        // Asegurar que cada archivo tenga la propiedad descripcion
                        const updatedFileList = newFileList.map(file => ({
                          ...file,
                          descripcion: file.descripcion || ''
                        }));
                        setFileList(updatedFileList);
                      }}
                      beforeUpload={() => false}
                      accept="image/*"
                      listType="picture-card"
                      onPreview={(file) => {
                        const imageUrl = getImageUrl(file.url || file.thumbUrl || (file.originFileObj ? URL.createObjectURL(file.originFileObj) : ''));
                        setPreviewImage(imageUrl);
                        setPreviewVisible(true);
                      }}
                      onRemove={(file) => {
                        const newFileList = fileList.filter((item) => item.uid !== file.uid);
                        setFileList(newFileList);
                        return true;
                      }}
                      customRequest={({ onSuccess }) => {
                        setTimeout(() => {
                          onSuccess("ok");
                        }, 0);
                      }}
                    >
                      {fileList.length < 10 && (
                        <div>
                          <InboxOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                          <div style={{ marginTop: 8 }}>Subir</div>
                        </div>
                      )}
                    </Upload>
                    {/* Mostrar campos de descripción para cada imagen */}
                    {fileList.length > 0 && (
                      <div style={{ marginTop: 16 }}>
                        {fileList.map((file, index) => (
                          <div key={file.uid || index} style={{ marginBottom: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                            <div style={{ marginBottom: 8, fontWeight: 500 }}>
                              Imagen {index + 1}
                            </div>
                            <Input
                              disabled={modoSoloLectura}
                              placeholder="Agregar descripción de la imagen (opcional)"
                              value={file.descripcion || ''}
                              onChange={(e) => {
                                const updatedFileList = fileList.map((f, idx) =>
                                  idx === index ? { ...f, descripcion: e.target.value } : f
                                );
                                setFileList(updatedFileList);
                              }}
                              style={{ width: '100%' }}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </Form.Item>

                  <Divider>Plano Interactivo - Marcado de Fallas Mecánicas</Divider>
                  <Form.Item name="fallas_mecanicas">
                    <PlanoCarro disabled={modoSoloLectura} />
                  </Form.Item>

                  <Divider>Checklist de Elementos del Vehículo</Divider>
                  <Form.Item shouldUpdate={(prevValues, currentValues) => {
                    // Forzar actualización cuando cambien los valores del checklist
                    const checklistKeys = checklistItems.map(item => item.key);
                    return checklistKeys.some(key => prevValues[key] !== currentValues[key]);
                  }}>
                    {() => {
                      // Agrupar elementos en pares para mostrar 2 por línea
                      const groupedItems = [];
                      for (let i = 0; i < checklistItems.length; i += 2) {
                        groupedItems.push(checklistItems.slice(i, i + 2));
                      }

                      return (
                        <div style={{ marginBottom: 16 }}>
                          {groupedItems.map((pair, pairIndex) => (
                            <Row gutter={[16, 8]} key={pairIndex} style={{ marginBottom: 8 }}>
                              {pair.map((item) => {
                                const currentValue = form.getFieldValue(item.key);
                                const isSI = currentValue === 'SI';
                                const isNO = currentValue === 'NO';

                                return (
                                  <Col span={12} key={item.key}>
                                    <Row gutter={[8, 0]}>
                                      <Col span={12} style={{ display: 'flex', alignItems: 'center', padding: '0 8px', fontSize: '13px' }}>
                                        {item.label}
                                      </Col>
                                      <Col span={6}>
                                        <Form.Item name={item.key} style={{ marginBottom: 0 }}>
                                          <div
                                            style={{
                                              padding: '8px 12px',
                                              border: '1px solid #d9d9d9',
                                              borderRadius: '2px',
                                              textAlign: 'center',
                                              cursor: modoSoloLectura ? 'not-allowed' : 'pointer',
                                              background: isSI ? '#e6f7ff' : '#fff',
                                              borderColor: isSI ? '#1890ff' : '#d9d9d9',
                                              transition: 'all 0.2s',
                                              userSelect: 'none',
                                              minHeight: '36px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              fontSize: '13px',
                                              opacity: modoSoloLectura ? 0.6 : 1,
                                            }}
                                            onClick={() => {
                                              if (modoSoloLectura) return;
                                              if (isSI) {
                                                form.setFieldsValue({ [item.key]: undefined });
                                              } else {
                                                form.setFieldsValue({ [item.key]: 'SI' });
                                              }
                                            }}
                                            onMouseEnter={(e) => {
                                              if (!isSI) {
                                                e.currentTarget.style.background = '#f5f5f5';
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if (!isSI) {
                                                e.currentTarget.style.background = '#fff';
                                              }
                                            }}
                                          >
                                            SI
                                          </div>
                                        </Form.Item>
                                      </Col>
                                      <Col span={6}>
                                        <Form.Item name={item.key} style={{ marginBottom: 0 }}>
                                          <div
                                            style={{
                                              padding: '8px 12px',
                                              border: '1px solid #d9d9d9',
                                              borderRadius: '2px',
                                              textAlign: 'center',
                                              cursor: modoSoloLectura ? 'not-allowed' : 'pointer',
                                              background: isNO ? '#fff1f0' : '#fff',
                                              borderColor: isNO ? '#ff4d4f' : '#d9d9d9',
                                              transition: 'all 0.2s',
                                              userSelect: 'none',
                                              minHeight: '36px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              fontSize: '13px',
                                              opacity: modoSoloLectura ? 0.6 : 1,
                                            }}
                                            onClick={() => {
                                              if (modoSoloLectura) return;
                                              if (isNO) {
                                                form.setFieldsValue({ [item.key]: undefined });
                                              } else {
                                                form.setFieldsValue({ [item.key]: 'NO' });
                                              }
                                            }}
                                            onMouseEnter={(e) => {
                                              if (!isNO) {
                                                e.currentTarget.style.background = '#f5f5f5';
                                              }
                                            }}
                                            onMouseLeave={(e) => {
                                              if (!isNO) {
                                                e.currentTarget.style.background = '#fff';
                                              }
                                            }}
                                          >
                                            NO
                                          </div>
                                        </Form.Item>
                                      </Col>
                                    </Row>
                                  </Col>
                                );
                              })}
                            </Row>
                          ))}
                        </div>
                      );
                    }}
                  </Form.Item>
                </div>
              </Tabs.TabPane>

              {/* Pestaña 3: Información de Pago */}
              <Tabs.TabPane tab="Información de Pago" key="3">
                <div style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
                  <Form.Item name="estado" label="Estado" initialValue="pendiente">
                    <Select disabled={modoSoloLectura}>
                      <Select.Option value="pendiente">Pendiente</Select.Option>
                      <Select.Option value="en_proceso">En Proceso</Select.Option>
                      <Select.Option value="completado">Completado</Select.Option>
                      <Select.Option value="cancelado">Anulado</Select.Option>
                    </Select>
                  </Form.Item>

                  <Divider>Información de Pago</Divider>

                  <Form.Item shouldUpdate={(prevValues, currentValues) =>
                    prevValues.costo !== currentValues.costo ||
                    prevValues.incluye_igv !== currentValues.incluye_igv ||
                    prevValues.monto_descuento !== currentValues.monto_descuento ||
                    prevValues.copago !== currentValues.copago ||
                    prevValues.comision !== currentValues.comision ||
                    prevValues.monto_adelanto !== currentValues.monto_adelanto
                  }>
                    {({ getFieldValue }) => {
                      const costo = getFieldValue('costo') || 0;
                      const incluyeIgv = parseIncluyeIgv(getFieldValue('incluye_igv'));
                      const descuento = parseFloat(getFieldValue('monto_descuento') || 0);
                      const copago = parseFloat(getFieldValue('copago') || 0);
                      const comision = parseFloat(getFieldValue('comision') || 0);
                      const adelanto = parseFloat(getFieldValue('monto_adelanto') || 0);

                      const { subtotal, igv, total } = calcularMontosServicio(costo, incluyeIgv);

                      // Calcular total de pagos (registrados o pendientes)
                      const totalPagos = editingServicio?.id
                        ? pagosServicio.reduce((sum, pago) => sum + parseFloat(pago.monto || 0), 0)
                        : pagosPendientes.reduce((sum, pago) => sum + parseFloat(pago.monto || 0), 0);
                      const saldo = total - descuento - copago - adelanto - totalPagos;

                      return (
                        <div style={{ marginBottom: 24 }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #d9d9d9', borderRadius: '4px', overflow: 'hidden' }}>
                            <thead>
                              <tr style={{ background: '#fafafa' }}>
                                <th style={{ padding: '12px', textAlign: 'left', borderBottom: '1px solid #d9d9d9', fontWeight: 600, width: '40%' }}>Concepto</th>
                                <th style={{ padding: '12px', textAlign: 'right', borderBottom: '1px solid #d9d9d9', fontWeight: 600, width: '60%' }}>Monto (S/)</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                                  <Form.Item name="costo" style={{ marginBottom: 0 }}>
                                    <span style={{ fontWeight: 500 }}>Monto Base</span>
                                  </Form.Item>
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                                  <Form.Item name="costo" style={{ marginBottom: 0 }}>
                                    <InputNumber
                                      disabled={modoSoloLectura}
                                      min={0}
                                      precision={2}
                                      style={{ width: '100%', textAlign: 'right' }}
                                      placeholder="0.00"
                                    />
                                  </Form.Item>
                                </td>
                              </tr>
                              <tr>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                                  <Form.Item name="incluye_igv" valuePropName="checked" initialValue={true} style={{ marginBottom: 0 }}>
                                    <Checkbox disabled={modoSoloLectura}>Incluye IGV</Checkbox>
                                  </Form.Item>
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', color: '#999' }}>
                                  -
                                </td>
                              </tr>
                              <tr style={{ background: '#f9f9f9' }}>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>
                                  Subtotal
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontWeight: 500 }}>
                                  {subtotal.toFixed(2)}
                                </td>
                              </tr>
                              <tr style={{ background: '#f9f9f9' }}>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>
                                  IGV (18%)
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontWeight: 500 }}>
                                  {igv.toFixed(2)}
                                </td>
                              </tr>
                              <tr style={{ background: '#e6f7ff', borderTop: '2px solid #1890ff', borderBottom: '2px solid #1890ff' }}>
                                <td style={{ padding: '12px', fontWeight: 600, fontSize: '15px' }}>
                                  Total
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, fontSize: '15px', color: '#1890ff' }}>
                                  {total.toFixed(2)}
                                </td>
                              </tr>
                              <tr>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                                  <Form.Item name="monto_descuento" style={{ marginBottom: 0 }}>
                                    <span style={{ fontWeight: 500 }}>Descuento</span>
                                  </Form.Item>
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                                  <Form.Item name="monto_descuento" style={{ marginBottom: 0 }}>
                                    <InputNumber
                                      disabled={modoSoloLectura}
                                      min={0}
                                      precision={2}
                                      style={{ width: '100%', textAlign: 'right' }}
                                      placeholder="0.00"
                                    />
                                  </Form.Item>
                                </td>
                              </tr>
                              <tr>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                                  <Form.Item name="copago" style={{ marginBottom: 0 }}>
                                    <span style={{ fontWeight: 500 }}>Copago</span>
                                  </Form.Item>
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                                  <Form.Item name="copago" style={{ marginBottom: 0 }}>
                                    <InputNumber
                                      disabled={modoSoloLectura}
                                      min={0}
                                      precision={2}
                                      style={{ width: '100%', textAlign: 'right' }}
                                      placeholder="0.00"
                                    />
                                  </Form.Item>
                                </td>
                              </tr>
                              <tr style={{ background: '#fffbe6' }}>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                                  <Form.Item name="comision" style={{ marginBottom: 0 }}>
                                    <span style={{ fontWeight: 500 }}>Comisión</span>
                                  </Form.Item>
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                                  <Form.Item name="comision" style={{ marginBottom: 0 }}>
                                    <InputNumber
                                      disabled={modoSoloLectura}
                                      min={0}
                                      precision={2}
                                      style={{ width: '100%', textAlign: 'right' }}
                                      placeholder="0.00"
                                    />
                                  </Form.Item>
                                </td>
                              </tr>
                              <tr>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0' }}>
                                  <Form.Item name="monto_adelanto" style={{ marginBottom: 0 }}>
                                    <span style={{ fontWeight: 500 }}>Adelanto</span>
                                  </Form.Item>
                                </td>
                                <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }}>
                                  <Form.Item name="monto_adelanto" style={{ marginBottom: 0 }}>
                                    <InputNumber
                                      disabled={modoSoloLectura}
                                      min={0}
                                      precision={2}
                                      style={{ width: '100%', textAlign: 'right' }}
                                      placeholder="0.00"
                                    />
                                  </Form.Item>
                                </td>
                              </tr>
                              {((editingServicio?.id ? pagosServicio : pagosPendientes).length > 0) && (
                                <tr style={{ background: '#f0f5ff' }}>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>
                                    {editingServicio?.id ? 'Pagos Registrados' : 'Pagos Pendientes'} ({(editingServicio?.id ? pagosServicio : pagosPendientes).length})
                                  </td>
                                  <td style={{ padding: '12px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontWeight: 500, color: '#1890ff' }}>
                                    - {totalPagos.toFixed(2)}
                                  </td>
                                </tr>
                              )}
                              <tr style={{ background: saldo >= 0 ? '#f6ffed' : '#fff1f0', borderTop: '2px solid #52c41a', borderBottom: '2px solid #52c41a' }}>
                                <td style={{ padding: '12px', fontWeight: 600, fontSize: '15px' }}>
                                  Saldo a Pagar
                                </td>
                                <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600, fontSize: '15px', color: saldo >= 0 ? '#52c41a' : '#ff4d4f' }}>
                                  {saldo.toFixed(2)}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      );
                    }}
                  </Form.Item>

                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name="estado_pago" label="Estado de Pago" initialValue="validacion">
                        <Select disabled={modoSoloLectura}>
                          <Select.Option value="validacion">Validación</Select.Option>
                          <Select.Option value="pagado">Pagado</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Divider>Método de Pago del Adelanto</Divider>
                  <Row gutter={16}>
                    <Col span={24}>
                      <Form.Item name="metodo_pago_adelanto_id" label="Método de Pago del Adelanto">
                        <Select disabled={modoSoloLectura} placeholder="Selecciona el método de pago" allowClear>
                          {metodosPago.map((metodo) => (
                            <Select.Option key={metodo.id} value={metodo.id}>
                              {metodo.nombre}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item shouldUpdate={(prevValues, currentValues) =>
                    prevValues.metodo_pago_adelanto_id !== currentValues.metodo_pago_adelanto_id
                  }>
                    {() => {
                      const metodoPagoAdelantoId = form.getFieldValue('metodo_pago_adelanto_id');
                      const metodoPagoAdelanto = metodosPago.find(m => m.id === metodoPagoAdelantoId);
                      const esEfectivoAdelanto = metodoPagoAdelanto?.nombre?.toLowerCase().includes('efectivo');
                      const mostrarAdelanto = metodoPagoAdelantoId && !esEfectivoAdelanto;

                      if (mostrarAdelanto) {
                        return (
                          <Row gutter={16}>
                            <Col span={24}>
                              <Form.Item label="Comprobantes de Adelanto">
                                <Upload
                                  disabled={modoSoloLectura}
                                  name="comprobantes_adelanto"
                                  listType="picture-card"
                                  fileList={comprobanteAdelanto}
                                  onChange={({ fileList: newFileList }) => {
                                    const updatedFileList = newFileList.map(file => ({
                                      ...file,
                                      descripcion: file.descripcion || ''
                                    }));
                                    setComprobanteAdelanto(updatedFileList);
                                  }}
                                  beforeUpload={() => false}
                                  accept="image/*"
                                  multiple
                                  onPreview={(file) => {
                                    window.open(file.url || file.thumbUrl, '_blank');
                                  }}
                                  customRequest={({ onSuccess }) => {
                                    setTimeout(() => {
                                      onSuccess("ok");
                                    }, 0);
                                  }}
                                  onRemove={(file) => {
                                    const newFileList = comprobanteAdelanto.filter((item) => item.uid !== file.uid);
                                    setComprobanteAdelanto(newFileList);
                                    return true;
                                  }}
                                >
                                  {comprobanteAdelanto.length < 10 && (
                                    <div>
                                      <PlusOutlined />
                                      <div style={{ marginTop: 8 }}>Subir</div>
                                    </div>
                                  )}
                                </Upload>
                                {/* Mostrar campos de descripción para cada comprobante */}
                                {comprobanteAdelanto.length > 0 && (
                                  <div style={{ marginTop: 16 }}>
                                    {comprobanteAdelanto.map((file, index) => (
                                      <div key={file.uid || index} style={{ marginBottom: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                                        <div style={{ marginBottom: 8, fontWeight: 500 }}>
                                          Comprobante {index + 1}
                                        </div>
                                        <Input
                                          disabled={modoSoloLectura}
                                          placeholder="Agregar descripción del comprobante (opcional)"
                                          value={file.descripcion || ''}
                                          onChange={(e) => {
                                            const updatedFileList = comprobanteAdelanto.map((f, idx) =>
                                              idx === index ? { ...f, descripcion: e.target.value } : f
                                            );
                                            setComprobanteAdelanto(updatedFileList);
                                          }}
                                          style={{ width: '100%' }}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </Form.Item>
                            </Col>
                          </Row>
                        );
                      }
                      return null;
                    }}
                  </Form.Item>

                  <Divider>Pagos Pendientes</Divider>
                  <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontWeight: 500 }}>
                      Pagos {editingServicio?.id ? 'Registrados' : 'Pendientes'}
                      ({(editingServicio?.id ? pagosServicio : pagosPendientes).length})
                    </span>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={handleAgregarPago}
                      disabled={modoSoloLectura}
                    >
                      Agregar Pago
                    </Button>
                  </div>
                  {editingServicio?.id ? (
                    <>
                      {pagosServicio.length > 0 ? (
                        <Table
                          dataSource={pagosServicio}
                          rowKey="id"
                          size="small"
                          pagination={false}
                          columns={[
                            {
                              title: 'Monto',
                              dataIndex: 'monto',
                              key: 'monto',
                              render: (monto) => `S/ ${parseFloat(monto || 0).toFixed(2)}`
                            },
                            {
                              title: 'Método de Pago',
                              dataIndex: 'metodo_pago_nombre',
                              key: 'metodo_pago_nombre'
                            },
                            {
                              title: 'Fecha',
                              dataIndex: 'fecha_pago',
                              key: 'fecha_pago',
                              render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-'
                            },
                            {
                              title: 'Estado',
                              dataIndex: 'estado',
                              key: 'estado',
                              render: (estado) => {
                                const colors = {
                                  pendiente: 'orange',
                                  validado: 'blue',
                                  pagado: 'green'
                                };
                                return <Tag color={colors[estado] || 'default'}>{estado?.toUpperCase() || '-'}</Tag>;
                              }
                            },
                            {
                              title: 'Acciones',
                              key: 'acciones',
                              render: (_, record) => (
                                <Space>
                                  <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEditarPago(record)}
                                    disabled={modoSoloLectura}
                                  >
                                    Editar
                                  </Button>
                                  <Popconfirm
                                    title="¿Estás seguro de eliminar este pago?"
                                    onConfirm={() => handleEliminarPago(record.id)}
                                    okText="Sí"
                                    cancelText="No"
                                  >
                                    <Button
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      disabled={modoSoloLectura}
                                    >
                                      Eliminar
                                    </Button>
                                  </Popconfirm>
                                </Space>
                              )
                            }
                          ]}
                        />
                      ) : (
                        <Empty description="No hay pagos registrados" />
                      )}
                    </>
                  ) : (
                    <>
                      {pagosPendientes.length > 0 ? (
                        <Table
                          dataSource={pagosPendientes}
                          rowKey="tempId"
                          size="small"
                          pagination={false}
                          columns={[
                            {
                              title: 'Monto',
                              dataIndex: 'monto',
                              key: 'monto',
                              render: (monto) => `S/ ${parseFloat(monto || 0).toFixed(2)}`
                            },
                            {
                              title: 'Método de Pago',
                              dataIndex: 'metodo_pago_nombre',
                              key: 'metodo_pago_nombre'
                            },
                            {
                              title: 'Fecha',
                              dataIndex: 'fecha_pago',
                              key: 'fecha_pago',
                              render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-'
                            },
                            {
                              title: 'Estado',
                              dataIndex: 'estado',
                              key: 'estado',
                              render: (estado) => {
                                const colors = {
                                  pendiente: 'orange',
                                  validado: 'blue',
                                  pagado: 'green'
                                };
                                return <Tag color={colors[estado] || 'default'}>{estado?.toUpperCase() || '-'}</Tag>;
                              }
                            },
                            {
                              title: 'Acciones',
                              key: 'acciones',
                              render: (_, record) => (
                                <Space>
                                  <Button
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                      setEditingPago(record);
                                      pagoForm.setFieldsValue({
                                        monto: record.monto,
                                        metodo_pago_id: record.metodo_pago_id,
                                        observaciones: record.observaciones,
                                        fecha_pago: record.fecha_pago ? dayjs(record.fecha_pago) : dayjs(),
                                        estado: record.estado
                                      });

                                      // Cargar comprobantes si existen
                                      if (record.comprobantes && Array.isArray(record.comprobantes) && record.comprobantes.length > 0) {
                                        const comprobantesFiles = record.comprobantes.map((img, index) => {
                                          const url = typeof img === 'object' && img.url ? img.url : img;
                                          return {
                                            uid: `comprobante-pendiente-${record.tempId}-${index}`,
                                            name: `comprobante-${index}.jpg`,
                                            status: 'done',
                                            url: getImageUrl(url),
                                            thumbUrl: getImageUrl(url),
                                            originalUrl: url,
                                            descripcion: typeof img === 'object' && img.descripcion ? img.descripcion : ''
                                          };
                                        });
                                        setComprobantesPagoForm(comprobantesFiles);
                                      } else {
                                        setComprobantesPagoForm([]);
                                      }

                                      setPagoModalVisible(true);
                                    }}
                                    disabled={modoSoloLectura}
                                  >
                                    Editar
                                  </Button>
                                  <Popconfirm
                                    title="¿Eliminar este pago?"
                                    onConfirm={() => handleEliminarPago(record.tempId)}
                                    okText="Sí"
                                    cancelText="No"
                                  >
                                    <Button
                                      size="small"
                                      danger
                                      icon={<DeleteOutlined />}
                                      disabled={modoSoloLectura}
                                    >
                                      Eliminar
                                    </Button>
                                  </Popconfirm>
                                </Space>
                              )
                            }
                          ]}
                        />
                      ) : (
                        <Empty description="No hay pagos pendientes. Agrega pagos que se guardarán al crear el servicio." />
                      )}
                    </>
                  )}
                </div>
              </Tabs.TabPane>

              {/* Pestaña 4: Seguimiento (solo si hay un servicio editándose) */}
              {editingServicio?.id && (
                <Tabs.TabPane tab="Seguimiento" key="4">
                  <div style={{ maxHeight: 'calc(90vh - 200px)', overflowY: 'auto' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', height: '500px' }}>
                      {/* Área de mensajes */}
                      <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #d9d9d9', borderRadius: '4px', padding: '16px', marginBottom: '16px', background: '#fafafa' }}>
                        <Spin spinning={cargandoSeguimiento}>
                          <List
                            dataSource={seguimientoMensajes}
                            renderItem={(mensaje) => {
                              const esMio = mensaje.usuario_id === user?.id;
                              return (
                                <List.Item style={{
                                  justifyContent: esMio ? 'flex-end' : 'flex-start',
                                  padding: '8px 0',
                                  borderBottom: '1px solid #f0f0f0'
                                }}>
                                  <div style={{
                                    maxWidth: '70%',
                                    background: esMio ? '#1890ff' : '#fff',
                                    color: esMio ? '#fff' : '#000',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                  }}>
                                    <div style={{ marginBottom: '8px', fontSize: '12px', opacity: 0.8 }}>
                                      {mensaje.usuario_nombre} ({mensaje.usuario_rol === 'admin' ? 'Admin' : 'Operador'})
                                      <span style={{ marginLeft: '8px' }}>
                                        {dayjs(mensaje.fecha_registro).format('DD/MM/YYYY HH:mm')}
                                      </span>
                                      {(esMio || user?.rol === 'admin') && (
                                        <Button
                                          type="link"
                                          danger
                                          size="small"
                                          onClick={() => handleEliminarMensaje(mensaje.id)}
                                          style={{ float: 'right', padding: 0, color: esMio ? '#fff' : '#ff4d4f' }}
                                        >
                                          Eliminar
                                        </Button>
                                      )}
                                    </div>
                                    {mensaje.mensaje && (
                                      <div style={{ marginBottom: mensaje.imagen ? '8px' : 0 }}>
                                        {mensaje.mensaje}
                                      </div>
                                    )}
                                    {mensaje.imagen && (
                                      <div>
                                        <Image
                                          src={getImageUrl(mensaje.imagen)}
                                          alt="Imagen de seguimiento"
                                          style={{ maxWidth: '100%', borderRadius: '4px' }}
                                          preview={{
                                            mask: 'Ver imagen'
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </List.Item>
                              );
                            }}
                          />
                        </Spin>
                      </div>

                      {/* Área de envío */}
                      <div style={{ borderTop: '1px solid #d9d9d9', paddingTop: '16px' }}>
                        <Space direction="vertical" style={{ width: '100%' }} size="middle">
                          <Input.TextArea
                            placeholder="Escribe un mensaje..."
                            value={nuevoMensaje}
                            onChange={(e) => setNuevoMensaje(e.target.value)}
                            rows={3}
                            onPressEnter={(e) => {
                              if (e.shiftKey) return; // Permitir nueva línea con Shift+Enter
                              e.preventDefault();
                              handleEnviarMensaje();
                            }}
                          />
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Upload
                              beforeUpload={(file) => {
                                setImagenSeguimiento(file);
                                return false;
                              }}
                              accept="image/*"
                              showUploadList={false}
                            >
                              <Button icon={<InboxOutlined />}>Adjuntar Imagen</Button>
                            </Upload>
                            {imagenSeguimiento && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '12px', color: '#666' }}>
                                  {imagenSeguimiento.name}
                                </span>
                                <Button
                                  type="link"
                                  danger
                                  size="small"
                                  onClick={() => setImagenSeguimiento(null)}
                                >
                                  Quitar
                                </Button>
                              </div>
                            )}
                            <Button
                              type="primary"
                              onClick={handleEnviarMensaje}
                              disabled={!nuevoMensaje.trim() && !imagenSeguimiento}
                            >
                              Enviar
                            </Button>
                          </div>
                        </Space>
                      </div>
                    </div>
                  </div>
                </Tabs.TabPane>
              )}
            </Tabs>
          </div>

          <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid #f0f0f0', background: '#fff', position: 'sticky', bottom: 0 }}>
            {!modoSoloLectura && (
              <Form.Item>
                <Space>
                  <Button type="primary" htmlType="submit" loading={submitting} disabled={submitting}>
                    {editingServicio ? 'Actualizar' : 'Crear'}
                  </Button>
                  <Button
                    onClick={() => {
                      setModalVisible(false);
                      form.resetFields();
                      setEditingServicio(null);
                      setModoSoloLectura(false);
                      setPagosServicio([]);
                      setPagosPendientes([]);
                      setComprobantesPago([]);
                      // Limpiar estados de ubicación
                      setDepartamentoContacto(null);
                      setProvinciaContacto(null);
                      setDistritoContacto(null);
                      setDepartamentoDestino(null);
                      setProvinciaDestino(null);
                      setDistritoDestino(null);
                    }}
                  >
                    Cancelar
                  </Button>
                </Space>
              </Form.Item>
            )}
            {modoSoloLectura && (
              <Form.Item>
                <Button
                  onClick={() => {
                    setModalVisible(false);
                    form.resetFields();
                    setEditingServicio(null);
                    setModoSoloLectura(false);
                    setPagosServicio([]);
                    setPagosPendientes([]);
                    setComprobantesPago([]);
                    // Limpiar estados de ubicación
                    setDepartamentoContacto(null);
                    setProvinciaContacto(null);
                    setDistritoContacto(null);
                    setDepartamentoDestino(null);
                    setProvinciaDestino(null);
                    setDistritoDestino(null);
                  }}
                >
                  Cerrar
                </Button>
              </Form.Item>
            )}
          </div>
        </Form>
      </Modal>

      {/* Modal para crear cliente */}
      <Modal
        title="Nuevo Cliente"
        open={clienteModalVisible}
        onCancel={() => {
          setClienteModalVisible(false);
          clienteForm.resetFields();
        }}
        footer={null}
      >
        <Form form={clienteForm} layout="vertical" onFinish={handleCrearCliente}>
          <Form.Item
            name="nombre"
            label="Nombre"
            rules={[{ required: true, message: 'Ingresa el nombre' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="apellido"
            label="Apellido"
            rules={[{ required: true, message: 'Ingresa el apellido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[{ type: 'email', message: 'Email inválido' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="telefono" label="Teléfono">
            <Input />
          </Form.Item>
          <Form.Item name="dni" label="DNI">
            <Input />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Crear
              </Button>
              <Button onClick={() => {
                setClienteModalVisible(false);
                clienteForm.resetFields();
              }}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal para crear vehículo */}
      <Modal
        title="Nuevo Vehículo"
        open={vehiculoModalVisible}
        onCancel={() => {
          setVehiculoModalVisible(false);
          vehiculoForm.resetFields();
        }}
        footer={null}
      >
        <Form form={vehiculoForm} layout="vertical" onFinish={handleCrearVehiculo}>
          <Form.Item
            name="placa"
            label="Placa"
            rules={[{ required: true, message: 'Ingresa la placa' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="marca"
            label="Marca"
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="modelo"
            label="Modelo"
          >
            <Input />
          </Form.Item>
          <Form.Item name="anio" label="Año">
            <InputNumber min={1900} max={new Date().getFullYear() + 1} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="color" label="Color">
            <Input />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                Crear
              </Button>
              <Button onClick={() => {
                setVehiculoModalVisible(false);
                vehiculoForm.resetFields();
              }}>
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal de preview de imágenes */}
      <Modal
        open={previewVisible}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        width="95%"
        style={{ maxWidth: 800 }}
        centered
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>

      {/* Modal para agregar/editar pago */}
      <Modal
        title={editingPago ? 'Editar Pago' : 'Agregar Pago'}
        open={pagoModalVisible}
        onCancel={() => {
          setPagoModalVisible(false);
          pagoForm.resetFields();
          setComprobantesPagoForm([]);
          setEditingPago(null);
        }}
        footer={null}
        width={600}
      >
        <Form form={pagoForm} layout="vertical" onFinish={handleGuardarPago}>
          <Form.Item
            name="monto"
            label="Monto"
            rules={[{ required: true, message: 'Ingresa el monto del pago' }]}
          >
            <InputNumber
              min={0}
              precision={2}
              style={{ width: '100%' }}
              placeholder="0.00"
              prefix="S/"
            />
          </Form.Item>

          <Form.Item
            name="metodo_pago_id"
            label="Método de Pago"
            rules={[{ required: true, message: 'Selecciona el método de pago' }]}
          >
            <Select placeholder="Selecciona el método de pago">
              {metodosPago.map((metodo) => (
                <Select.Option key={metodo.id} value={metodo.id}>
                  {metodo.nombre}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="fecha_pago"
            label="Fecha de Pago"
            initialValue={dayjs()}
          >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
          </Form.Item>

          <Form.Item
            name="estado"
            label="Estado"
            initialValue="pendiente"
          >
            <Select>
              <Select.Option value="pendiente">Pendiente</Select.Option>
              <Select.Option value="validado">Validado</Select.Option>
              <Select.Option value="pagado">Pagado</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="observaciones"
            label="Observaciones"
          >
            <Input.TextArea rows={3} placeholder="Observaciones del pago (opcional)" />
          </Form.Item>

          <Form.Item label="Comprobantes">
            <Upload
              name="comprobantes"
              listType="picture-card"
              fileList={comprobantesPagoForm}
              onChange={({ fileList: newFileList }) => {
                const updatedFileList = newFileList.map(file => ({
                  ...file,
                  descripcion: file.descripcion || ''
                }));
                setComprobantesPagoForm(updatedFileList);
              }}
              beforeUpload={() => false}
              accept="image/*"
              multiple
              onPreview={(file) => {
                window.open(file.url || file.thumbUrl, '_blank');
              }}
              customRequest={({ onSuccess }) => {
                setTimeout(() => {
                  onSuccess("ok");
                }, 0);
              }}
              onRemove={(file) => {
                const newFileList = comprobantesPagoForm.filter((item) => item.uid !== file.uid);
                setComprobantesPagoForm(newFileList);
                return true;
              }}
            >
              {comprobantesPagoForm.length < 10 && (
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Subir</div>
                </div>
              )}
            </Upload>
            {comprobantesPagoForm.length > 0 && (
              <div style={{ marginTop: 16 }}>
                {comprobantesPagoForm.map((file, index) => (
                  <div key={file.uid || index} style={{ marginBottom: 16, padding: 12, border: '1px solid #d9d9d9', borderRadius: 4 }}>
                    <div style={{ marginBottom: 8, fontWeight: 500 }}>
                      Comprobante {index + 1}
                    </div>
                    <Input
                      placeholder="Agregar descripción del comprobante (opcional)"
                      value={file.descripcion || ''}
                      onChange={(e) => {
                        const updatedFileList = comprobantesPagoForm.map((f, idx) =>
                          idx === index ? { ...f, descripcion: e.target.value } : f
                        );
                        setComprobantesPagoForm(updatedFileList);
                      }}
                      style={{ width: '100%' }}
                    />
                  </div>
                ))}
              </div>
            )}
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingPago ? 'Actualizar' : 'Guardar'}
              </Button>
              <Button
                onClick={() => {
                  setPagoModalVisible(false);
                  pagoForm.resetFields();
                  setComprobantesPagoForm([]);
                  setEditingPago(null);
                }}
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Servicios;
